import { Helmet } from "react-helmet-async";
import React from "react";
import { useTranslation } from "react-i18next";

import { absoluteUrl } from "../utils/siteOrigin";

export type PageSeoProps = {
  /** i18n key for document title (must exist in ar.json and en.json). */
  titleKey: string;
  /** i18n key for meta description (must exist in ar.json and en.json). */
  descriptionKey: string;
  /** Path only, e.g. `/` or `/party/modes` (used for canonical and og:url). */
  path: string;
  /** Optional JSON-LD object (e.g. WebSite); serialized as application/ld+json. */
  jsonLd?: Record<string, unknown>;
};

/**
 * Per-route title, description, canonical, and Open Graph tags for crawlers and link previews.
 */
const PageSeo: React.FC<PageSeoProps> = ({
  titleKey,
  descriptionKey,
  path,
  jsonLd,
}) => {
  const { t } = useTranslation();
  const title = t(titleKey);
  const description = t(descriptionKey);
  const url = absoluteUrl(path);

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {url.startsWith("http") ? <link rel="canonical" href={url} /> : null}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      {url.startsWith("http") ? <meta property="og:url" content={url} /> : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {jsonLd && url.startsWith("http") ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
};

export default PageSeo;
